// ==============================
// = Age Gate Component
// ==============================
// 
// Overview:
// This component contains logic for an age gate component.
// Simplified version with Yes/No buttons instead of date input.
//
//

// Create a global variable for the age gate
window.ageGate = null;

// Immediate check for the cookie - before any DOM content loads
(function checkAgeGateCookie() {
  const getCookieValue = (name) => (
    document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || ''
  );
  
  const isOfAge = getCookieValue('validAge');
  
  // Add a CSS class to the HTML element to control initial visibility
  if (isOfAge) {
    document.documentElement.classList.add('age-verified');
  }
})();

class AgeGate {
  constructor(minAge, redirectLink) {
    this.minAge = minAge;
    this.redirectLink = redirectLink || "https://www.google.com"; // Fallback redirect URL
    this.touchStartY = 0;
    this.scrollPosition = 0;
    
    // Bind methods to ensure proper 'this' context
    this.preventTouchScroll = this.preventTouchScroll.bind(this);
    this.recordTouchStart = this.recordTouchStart.bind(this);
  }
  
  init() {
    const ageGateElement = document.getElementById("age-gate");
    if (!ageGateElement) return;
    
    // Check If Cookie Set
    const getCookieValue = (name) => (
      document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || ''
    );
    
    const isOfAge = getCookieValue('validAge');
    
    if (isOfAge) {
      // User already verified, hide the age gate immediately
      ageGateElement.classList.add("hidden");
      document.documentElement.classList.add('age-verified');
    } else {
      // Make sure age gate is visible
      ageGateElement.classList.remove("hidden");
      document.documentElement.classList.remove('age-verified');
      this.lockScroll();
      this.addTouchEventListeners();
    }
    
    // Set mobile background if available
    this.setMobileBackground();
    
    // Handle resize events for responsive background
    window.addEventListener('resize', this.setMobileBackground.bind(this));
  }
  
  lockScroll() {
    // Store the current scroll position before locking
    this.scrollPosition = window.pageYOffset;
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.classList.add("lock-scroll");
    document.documentElement.classList.add("no-scroll");
  }
  
  unlockScroll() {
    document.body.classList.remove("lock-scroll");
    document.documentElement.classList.remove("no-scroll");
    document.body.style.top = '';
    window.scrollTo(0, this.scrollPosition || 0);
    this.removeTouchEventListeners();
  }
  
  addTouchEventListeners() {
    document.addEventListener('touchstart', this.recordTouchStart, { passive: false });
    document.addEventListener('touchmove', this.preventTouchScroll, { passive: false });
  }
  
  removeTouchEventListeners() {
    document.removeEventListener('touchstart', this.recordTouchStart);
    document.removeEventListener('touchmove', this.preventTouchScroll);
  }
  
  recordTouchStart(e) {
    // Store the starting Y position
    this.touchStartY = e.touches[0].clientY;
  }
  
  preventTouchScroll(e) {
    // Get the age gate content element
    const ageGateContent = document.querySelector('.age-gate-content');
    if (!ageGateContent) return;
    
    // Only allow scrolling inside the age-gate-content element
    if (!ageGateContent.contains(e.target)) {
      e.preventDefault();
      return;
    }
    
    // Check if we're at the top or bottom of the scrollable area
    const scrollTop = ageGateContent.scrollTop;
    const scrollHeight = ageGateContent.scrollHeight;
    const clientHeight = ageGateContent.clientHeight;
    const touchY = e.touches[0].clientY;
    const touchDelta = touchY - this.touchStartY;
    
    // Prevent overscrolling
    if ((scrollTop <= 0 && touchDelta > 0) || 
        (scrollTop + clientHeight >= scrollHeight && touchDelta < 0)) {
      e.preventDefault();
    }
  }
  
  setMobileBackground() {
    const mainContent = document.querySelector('.age-gate-main-content');
    if (!mainContent) return;
    
    const mobileBgUrl = mainContent.getAttribute('data-mobile-bg');
    
    if (mobileBgUrl && mobileBgUrl.trim() !== '') {
      mainContent.style.setProperty('--mobile-bg-image', `url('${mobileBgUrl}')`);
    }
  }
  
  confirmAge() {
    // User confirms they are of valid age
    this.setAgeGateCookie();
    document.getElementById("age-gate").classList.add("hidden");
    document.documentElement.classList.add('age-verified');
    this.unlockScroll();
    
    // Send Data Layer Event if exists
    this.dataLayerEvent();
  }
  
  rejectAge() {
    // User confirms they are not of valid age
    // Redirect to the URL specified in settings
    if (this.redirectLink && this.redirectLink.trim() !== '') {
      window.location.replace(this.redirectLink);
    } else {
      console.error("No redirect URL specified for underage visitors");
      // Fallback to a default site or show a message
      window.location.replace("https://www.google.com");
    }
  }
  
  setAgeGateCookie() {
    const days = 15; // Number of days until they must go through the age checker again
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "; expires=" + date.toGMTString();
    document.cookie = 'validAge=true;' + expires + "; path=/";
  }
  
  dataLayerEvent() {
    // Send Data Layer Event if dataLayer exists
    if (typeof dataLayer !== 'undefined') {
      dataLayer.push({
        'event': 'ageGateCompleted',
        'ageGateStatus': 'verified'
      });
    }
  }
}

// Initialize the age gate
document.addEventListener("DOMContentLoaded", function() {
  const ageGateElement = document.querySelector('#age-gate');
  
  if (ageGateElement) {
    const minAge = ageGateElement.dataset.minAge || 21;
    const redirectUrl = ageGateElement.dataset.redirectUrl || "";
    
    // Create the global instance
    window.ageGate = new AgeGate(minAge, redirectUrl);
    
    // Initialize the age gate
    window.ageGate.init();
  }
});